<template>
    <div class="jetty-checklist-page">
        <div v-if="isFormPage" class="form-page-wrapper">
            <router-view />
        </div>

        <div v-show="!isFormPage" class="checklist-shell">
            <div class="shell-header">
                <div>
                    <h2 class="page-title">Jetty Checklist</h2>
                    <p class="page-subtitle" v-if="activeTab == 'today'">
                        {{ pendingCount }} jetties pending today
                    </p>
                    <p class="page-subtitle" v-if="activeTab == 'history'">
                        View pass records
                    </p>
                    <p class="page-subtitle" v-if="activeTab == 'activity'">
                        {{ activityPagination.total }} activity entries
                    </p>
                </div>

                <div class="tabs-wrapper">
                    <el-tabs v-model="activeTab" class="custom-tabs">
                        <el-tab-pane name="today">
                            <template #label>
                                <span class="tab-label">
                                    <el-icon class="el-icon-date" style="padding-right: 4px;"></el-icon>
                                    Today
                                </span>
                            </template>
                        </el-tab-pane>

                        <el-tab-pane name="history">
                            <template #label>
                                <span class="tab-label">
                                    <el-icon class="el-icon-time" style="padding-right: 4px;"></el-icon>
                                    History
                                </span>
                            </template>
                        </el-tab-pane>

                        <el-tab-pane name="activity">
                            <template #label>
                                <span class="tab-label">
                                    <el-icon class="el-icon-document" style="padding-right: 4px;"></el-icon>
                                    Activity Log
                                </span>
                            </template>
                        </el-tab-pane>
                    </el-tabs>
                </div>
            </div>

            <div class="content">
                <component ref="tabContent" :key="activeTab" :is="tabComponent"
                    @update-completed="completedCount = $event" @activity-page-state="handleActivityPageState"
                    @history-page-state="handleHistoryPageState" />
            </div>

            <div v-if="activeTab === 'activity' && activityPagination.total > 0" class="pagination-bar">
                <div class="pagination-left">
                    <span class="pagination-info">
                        Showing {{ activityStartEntry }} - {{ activityEndEntry }} of {{ activityPagination.total }}
                    </span>

                    <select v-model.number="activityPagination.pageSize" class="page-size-select"
                        @change="handleActivityPageSizeChange">
                        <option :value="5">5 / page</option>
                        <option :value="10">10 / page</option>
                        <option :value="20">20 / page</option>
                        <option :value="50">50 / page</option>
                    </select>
                </div>

                <div class="pagination-right">
                    <button class="page-nav-btn"
                        :disabled="activityPagination.pageNum === 1 || activityPagination.loading"
                        @click="goToActivityPage(activityPagination.pageNum - 1)">
                        <i class="pi pi-angle-left"></i>
                    </button>

                    <button v-for="page in activityVisiblePages" :key="page" class="page-number-btn"
                        :class="{ active: page === activityPagination.pageNum, dots: page === '...' }"
                        :disabled="page === '...' || activityPagination.loading"
                        @click="page !== '...' && goToActivityPage(page)">
                        {{ page }}
                    </button>

                    <button class="page-nav-btn"
                        :disabled="activityPagination.pageNum === activityTotalPages || activityTotalPages === 0 || activityPagination.loading"
                        @click="goToActivityPage(activityPagination.pageNum + 1)">
                        <i class="pi pi-angle-right"></i>
                    </button>
                </div>
            </div>

            <div v-if="activeTab === 'history' && historyPagination.total > 0 && !historyPagination.hasSelectedRecord" class="pagination-bar">
                <div class="pagination-left">
                    <span class="pagination-info">
                        Showing {{ historyStartEntry }} - {{ historyEndEntry }} of {{ historyPagination.total }}
                    </span>

                    <select v-model.number="historyPagination.pageSize" class="page-size-select"
                        @change="handleHistoryPageSizeChange">
                        <option :value="5">5 / page</option>
                        <option :value="10">10 / page</option>
                        <option :value="20">20 / page</option>
                        <option :value="50">50 / page</option>
                    </select>
                </div>

                <div class="pagination-right">
                    <button class="page-nav-btn"
                        :disabled="historyPagination.pageNum === 1 || historyPagination.loading"
                        @click="goToHistoryPage(historyPagination.pageNum - 1)">
                        <i class="pi pi-angle-left"></i>
                    </button>

                    <button v-for="page in historyVisiblePages" :key="page" class="page-number-btn"
                        :class="{ active: page === historyPagination.pageNum, dots: page === '...' }"
                        :disabled="page === '...' || historyPagination.loading"
                        @click="page !== '...' && goToHistoryPage(page)">
                        {{ page }}
                    </button>

                    <button class="page-nav-btn"
                        :disabled="historyPagination.pageNum === historyTotalPages || historyTotalPages === 0 || historyPagination.loading"
                        @click="goToHistoryPage(historyPagination.pageNum + 1)">
                        <i class="pi pi-angle-right"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ActivityTab from './component/activityTab.vue';
import HistoryTab from './component/historyTab.vue';
import TodayTab from './component/todayTab.vue';

export default {
    name: "JettyChecklist",
    data() {
        return {
            activeTab: 'today',
            completedCount: 0,
            activityPagination: {
                total: 0,
                pageNum: 1,
                pageSize: 10,
                loading: false
            },
            historyPagination: {
                total: 0,
                pageNum: 1,
                pageSize: 10,
                loading: false,
                hasSelectedRecord: false
            }
        };
    },
    computed: {
        tabComponent() {
            if (this.activeTab === "today") return TodayTab
            if (this.activeTab === "history") return HistoryTab
            return ActivityTab
        },
        isFormPage() {
            return this.$route.name === 'JettyForm'
        },
        pendingCount() {
            const total = 15
            return total - this.completedCount
        },
        activityTotalPages() {
            if (!this.activityPagination.total || !this.activityPagination.pageSize) return 0
            return Math.ceil(this.activityPagination.total / this.activityPagination.pageSize)
        },
        activityStartEntry() {
            if (!this.activityPagination.total) return 0
            return (this.activityPagination.pageNum - 1) * this.activityPagination.pageSize + 1
        },
        activityEndEntry() {
            if (!this.activityPagination.total) return 0
            return Math.min(
                this.activityPagination.pageNum * this.activityPagination.pageSize,
                this.activityPagination.total
            )
        },
        activityVisiblePages() {
            const total = this.activityTotalPages
            const current = this.activityPagination.pageNum

            if (total <= 7) {
                return Array.from({ length: total }, function (_, index) {
                    return index + 1
                })
            }

            if (current <= 4) {
                return [1, 2, 3, 4, 5, '...', total]
            }

            if (current >= total - 3) {
                return [1, '...', total - 4, total - 3, total - 2, total - 1, total]
            }

            return [1, '...', current - 1, current, current + 1, '...', total]
        },
        historyTotalPages() {
            if (!this.historyPagination.total || !this.historyPagination.pageSize) return 0
            return Math.ceil(this.historyPagination.total / this.historyPagination.pageSize)
        },

        historyStartEntry() {
            if (!this.historyPagination.total) return 0
            return (this.historyPagination.pageNum - 1) * this.historyPagination.pageSize + 1
        },

        historyEndEntry() {
            if (!this.historyPagination.total) return 0
            return Math.min(
                this.historyPagination.pageNum * this.historyPagination.pageSize,
                this.historyPagination.total
            )
        },

        historyVisiblePages() {
            const total = this.historyTotalPages
            const current = this.historyPagination.pageNum

            if (total <= 7) {
                return Array.from({ length: total }, function (_, index) {
                    return index + 1
                })
            }

            if (current <= 4) {
                return [1, 2, 3, 4, 5, '...', total]
            }

            if (current >= total - 3) {
                return [1, '...', total - 4, total - 3, total - 2, total - 1, total]
            }

            return [1, '...', current - 1, current, current + 1, '...', total]
        },
    },
    methods: {
        handleActivityPageState(payload) {
            this.activityPagination = {
                ...this.activityPagination,
                total: payload.total || 0,
                pageNum: payload.pageNum || 1,
                pageSize: payload.pageSize || 10,
                loading: !!payload.loading
            }
        },

        goToActivityPage(page) {
            if (this.activeTab !== 'activity') return
            if (page < 1 || page > this.activityTotalPages) return
            if (!this.$refs.tabContent || !this.$refs.tabContent.setPage) return

            this.$refs.tabContent.setPage(page)
        },

        handleActivityPageSizeChange() {
            if (this.activeTab !== 'activity') return
            if (!this.$refs.tabContent || !this.$refs.tabContent.setPageSize) return

            this.$refs.tabContent.setPageSize(this.activityPagination.pageSize)
        },
        handleHistoryPageState(payload) {
            this.historyPagination = {
                ...this.historyPagination,
                total: payload.total || 0,
                pageNum: payload.pageNum || 1,
                pageSize: payload.pageSize || 10,
                loading: !!payload.loading,
                hasSelectedRecord: !!payload.hasSelectedRecord
            }
        },

        goToHistoryPage(page) {
            if (this.activeTab !== 'history') return
            if (page < 1 || page > this.historyTotalPages) return
            if (!this.$refs.tabContent || !this.$refs.tabContent.setPage) return

            this.$refs.tabContent.setPage(page)
        },

        handleHistoryPageSizeChange() {
            if (this.activeTab !== 'history') return
            if (!this.$refs.tabContent || !this.$refs.tabContent.setPageSize) return

            this.$refs.tabContent.setPageSize(this.historyPagination.pageSize)
        },
    }
}
</script>

<style scoped lang="scss">
.jetty-checklist-page {
    height: calc(100vh - 90px);
    overflow: hidden;
}

.form-page-wrapper {
    height: 100%;
}

.checklist-shell {
    margin: 8px 16px;
    height: calc(100% - 30px);
    background-color: white;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.shell-header {
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
    padding: 16px 16px 0 16px;
    gap: 12px;
}

.page-title {
    font-size: 20px;
    line-height: 1.2;
    margin-bottom: 4px;
    font-weight: 600;
}

.page-subtitle {
    font-size: 12px;
    font-weight: 400;
    color: #6b7280;
}

.tabs-wrapper {
    background: #eef2f6;
    padding: 4px;
    border-radius: 14px;
    width: fit-content;
    height: 48px;
    flex-shrink: 0;
}

/* remove default line */
::v-deep .el-tabs__header {
    margin: 0;
}

::v-deep .el-tabs__nav-wrap::after {
    display: none;
}

::v-deep .el-tabs__item {
    border-radius: 12px;
    place-content: center;
    font-weight: 600;
    font-size: 16px;
    color: #6b7b8e;
    padding: 0;
}

::v-deep .el-tabs__item.is-active {
    background: white;
    color: #303133;
}

::v-deep .el-tabs__active-bar {
    display: none;
}

.tab-label {
    display: flex;
    align-items: center;
    height: 20px;
    padding: 0;
    padding-left: 10px;
    padding-right: 10px;
}

.content {
    flex: 1;
    min-height: 0;
    padding-top: 4px;
    background-color: #e9e9e9;
    overflow-y: scroll;
}

::v-deep .content>* {
    min-height: 0;
}

.pagination-bar {
    flex-shrink: 0;
    position: sticky;
    bottom: 0;
    z-index: 10;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    border-top: 1px solid #edf1f5;
    background: #fff;
    box-shadow: 0 -6px 16px rgba(15, 23, 42, 0.04);
    flex-wrap: wrap;
}

.pagination-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.pagination-info {
    font-size: 14px;
    font-weight: 600;
    color: #61728d;
}

.page-size-select {
    height: 40px;
    border: 1px solid #cfd6e0;
    border-radius: 14px;
    padding: 0 40px 0 12px;
    font-size: 14px;
    color: #161b26;
    background-color: #fff;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238b97aa' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-size: 12px;
}

.pagination-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.page-nav-btn,
.page-number-btn {
    min-width: 40px;
    height: 40px;
    padding: 0 12px;
    border: 1px solid #d9dee7;
    border-radius: 12px;
    background: #fff;
    color: #4f5f77;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.page-number-btn.active {
    background: #1887e0;
    border-color: #1887e0;
    color: #fff;
}

.page-number-btn.dots {
    cursor: default;
    color: #98a3b3;
}

.page-nav-btn:disabled,
.page-number-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

@media (max-width: 768px) {
    .checklist-shell {
        margin: 8px;
        height: calc(100% - 16px);
    }

    .shell-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .tabs-wrapper {
        width: 100%;
        height: auto;
    }

    .pagination-bar {
        padding: 12px 16px;
    }
}
</style>